import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF as JsPdf } from 'jspdf';
import { saveAs } from 'file-saver-es';
import { Workbook } from 'exceljs';
import './employee-list.scss';
import DataGrid, {
  DataGridRef,
  Sorting, HeaderFilter, Scrolling, SearchPanel,
  Export, Column, Toolbar, Item, LoadPanel,
  DataGridTypes,
  Editing, FilterRow,
  Form,
  RequiredRule,
  Lookup,
  Popup,
  Button as ColumnButton,
} from 'devextreme-react/data-grid';
import { Item as FormItem } from 'devextreme-react/form';
import Button from 'devextreme-react/button';
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter';
import { exportDataGrid as exportDataGridToXLSX } from 'devextreme/excel_exporter';
import { Relation } from '../../types/crm-contact';
import { Avatar } from '../../components';
import { useAuth } from '../../contexts/auth';
import DataSource from 'devextreme/data/data_source';
import 'devextreme/data/odata/store';
import { POSITION_LIST } from '../../shared/constants';
import { createEmployee, deleteEmployee } from '../../api/employees';
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';

const onExporting = (e: DataGridTypes.ExportingEvent) => {
  if (e.format === 'pdf') {
    const doc = new JsPdf();
    exportDataGridToPdf({
      jsPDFDocument: doc,
      component: e.component,
    }).then(() => {
      doc.save('Contacts.pdf');
    });
  } else {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Contacts');

    exportDataGridToXLSX({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Contacts.xlsx');
      });
    });
    e.cancel = true;
  }
};

const exportFormats = ['xlsx', 'pdf'];

export const EmployeeList = () => {
    const [gridDataSource, setGridDataSource] = useState<DataSource<Relation[], string>>();
    const gridRef = useRef<DataGridRef>(null);
    const navigate = useNavigate();
    const { user, signOut } = useAuth();
    const dataSource = new DataSource({
        store: {
            type: 'odata',
            version: 4,
            key: 'Oid',
            keyType: 'Guid',
            url: `${process.env.REACT_APP_URL}/api/odata/Employee`,
            errorHandler: (error) => {
                if (error.httpStatus === 401) {
                    signOut(true);
                }
            },
            beforeSend: (e) => {
                e.headers = {
                    'Authorization': `Bearer ${user?.token}`
                };
            }
        },
        select: [
            'Oid',
            'FirstName',
            'LastName',
            'DateOfBirth',
            'Email',
            'PhoneNumber',
            'Address',
            'City',
            'PostalCode',
            'HireDate',
            'Position',
            'CreatedAt',
            'SchedulerColor',
            'FullName',
            'Active'
        ],
        expand: [],
    });

  useEffect(() => {
      setGridDataSource(dataSource);
  }, []);

  const onAddContactClick = useCallback(() => {
    gridRef.current?.instance().addRow();
  }, []);

  const onRowInserting = useCallback((e: DataGridTypes.RowInsertingEvent) => {
    e.cancel = createEmployee(e.data, user?.token ?? '')
      .then(() => {
        notify({ message: 'Medewerker aangemaakt', position: { at: 'bottom center', my: 'bottom center' } }, 'success');
        gridRef.current?.instance().refresh();
      })
      .catch((error) => {
        notify({ message: `Fout bij aanmaken: ${error?.response?.data || error.message}`, position: { at: 'bottom center', my: 'bottom center' } }, 'error', 3000);
      }) as Promise<void>;
  }, [user?.token]);

  const onRowClick = useCallback(({ data }: DataGridTypes.RowClickEvent) => {
    navigate('/employee-details', { state: { employeeOid: data.Oid._value.toString(), backTo: 'empolyeeist' } });
  }, []);

  const refresh = useCallback(() => {
    gridRef.current?.instance().refresh();
  }, []);

  const onDeleteEmployee = useCallback(async(e: any) => {
    const row = e.row?.data;
    const oid = row?.Oid?._value?.toString() ?? String(row?.Oid);
    const name = row?.FullName || `${row?.FirstName ?? ''} ${row?.LastName ?? ''}`.trim();
    const result = await confirm(`Weet u zeker dat u '${name}' wilt verwijderen? Dit verwijdert ook het gebruikersaccount.`, 'Medewerker verwijderen');
    if (!result) return;
    try {
      await deleteEmployee(oid, user?.token ?? '');
      notify({ message: 'Medewerker verwijderd', position: { at: 'bottom center', my: 'bottom center' } }, 'success');
      gridRef.current?.instance().refresh();
    } catch (error: any) {
      notify({ message: `Fout bij verwijderen: ${error?.response?.data || error.message}`, position: { at: 'bottom center', my: 'bottom center' } }, 'error', 3000);
    }
  }, [user?.token]);

  const avatarGridCell = (data: DataGridTypes.ColumnCellTemplateData) => { return (<Avatar name={data.data.FullName} backgroundColor={data.data.SchedulerColor} size={25} />); };

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-contact-list'>
        <DataGrid
          className='grid'
          noDataText=''
          focusedRowEnabled
          height='100%'
          dataSource={gridDataSource}
          onRowClick={onRowClick}
          onRowInserting={onRowInserting}
          onExporting={onExporting}
          allowColumnReordering
          ref={gridRef}>
          <LoadPanel showPane={false} />
          <SearchPanel width={240} visible placeholder='Employee Search' />
          <Export allowExportSelectedData formats={exportFormats} />
          <HeaderFilter visible allowSearch />
          <FilterRow visible />
          <Sorting mode='multiple' />
          <Scrolling mode='virtual' />
          <Editing
              useIcons
              allowAdding
              mode='popup'>
          <Form>
            <FormItem itemType='group' colCount={2} colSpan={2}>
              <FormItem dataField='FirstName' caption='Voornaam'>
                <RequiredRule message='Dit veld is verplicht!' />
              </FormItem>
              <FormItem dataField='LastName' caption='Achternaam'>
                <RequiredRule message='Dit veld is verplicht!' />
              </FormItem>
              <FormItem dataField='Position' editorType='dxSelectBox' colSpan={2}>
                <RequiredRule message='Dit veld is verplicht!' />
              </FormItem>
              <FormItem dataField='SchedulerColor' editorType='dxColorBox' colSpan={2}>
                <RequiredRule message='Dit veld is verplicht!' />
              </FormItem>
              <FormItem dataField='Email' colSpan={2}>
                <RequiredRule message='Dit veld is verplicht!' />
              </FormItem>
              <FormItem dataField='PhoneNumber'>
                <RequiredRule message='Dit veld is verplicht!' />
              </FormItem>
              <FormItem dataField='Address' />
              <FormItem dataField='City' />
              <FormItem dataField='PostalCode' />
              <FormItem dataField='DateOfBirth' caption='Verjaardag' editorType='dxDateBox' />
              <FormItem dataField='HireDate' caption='Datum in dienst' editorType='dxDateBox' />
            </FormItem>
          </Form>
                <Popup
                    width={600}
                    height={650}
                    showTitle
                    title='Nieuwe medewerker' />
              </Editing>
          <Toolbar>
            <Item location='before'>
              <div className='grid-header'>Medewerkers</div>
            </Item>
            <Item location='after' locateInMenu='auto'>
             <Button
                icon='plus'
                text='Medewerker'
                type='default'
                stylingMode='contained'
                onClick={onAddContactClick}
              />
            </Item>
            <Item
              location='after'
              locateInMenu='auto'
              showText='inMenu'
              widget='dxButton'
            >
              <Button
                icon='refresh'
                text='Refresh'
                stylingMode='text'
                onClick={refresh}
              />
            </Item>
            <Item location='after' locateInMenu='auto' />
            <Item name='exportButton' />
            <Item location='after' locateInMenu='auto' />
            <Item name='columnChooserButton' locateInMenu='auto' />
            <Item name='searchPanel' locateInMenu='auto' />
            </Toolbar>
          <Column dataField='FirstName' caption='Voornaam' visible={false} />
          <Column dataField='LastName' caption='Achternaam' visible={false} />
          <Column dataField='Address' caption='Adres' visible={false} />
          <Column dataField='PostalCode' caption='Postcode' visible={false} />
          <Column dataField='HireDate' caption='Datum in dienst' visible={false} />
          <Column sortOrder='asc' dataField='FullName' caption='Medewerker' />
          <Column dataField='Position' width={130} caption='Functie'>
          <Lookup dataSource={POSITION_LIST} />
          </Column>
          <Column dataField='SchedulerColor' width={130} caption='Agenda kleur' cellRender={avatarGridCell} />
          <Column dataField='DateOfBirth' dataType='date' caption='Geboortedatum' />
          <Column dataField='Email' caption='Email' />
          <Column dataField='PhoneNumber' caption='Telefoon Number' />
          <Column dataField='City' caption='Woonplaats' />
          <Column dataField='Active' caption='Actief' />
          <Column type='buttons' width={70}>
            <ColumnButton icon='trash' hint='Verwijderen' onClick={onDeleteEmployee} />
          </Column>
        </DataGrid>
      </div>
    </div>
  );
};
